/**
 * Ownership arithmetic in integer basis points (1% = 100 bp).
 *
 * B15 (2026-09-21): the old 0.01 float tolerance accepted 50 + 49.99 and
 * 60 + 39.99 (a company filed at 99.99%) while refusing 33.33 + 66.66 over
 * float noise. Every comparison is now an integer sum that must be exactly
 * 10000.
 */

export const FULL_OWNERSHIP_BASIS_POINTS = 10_000;

/**
 * Round half-up at the second decimal. Shifting through the exponent string
 * ("50.005e2") avoids the binary error that makes `50.005 * 100` 5000.4999…
 */
export function percentToBasisPoints(
  percent: number | null | undefined,
): number {
  if (typeof percent !== "number" || !Number.isFinite(percent)) return 0;
  const shifted = Number(`${percent}e2`);
  return Math.round(Number.isFinite(shifted) ? shifted : percent * 100);
}

export function roundPercent(percent: number): number {
  return percentToBasisPoints(percent) / 100;
}

export function ownershipTotalBasisPoints(
  percentages: readonly (number | null | undefined)[],
): number {
  return percentages.reduce<number>(
    (sum, percent) => sum + percentToBasisPoints(percent),
    0,
  );
}

/** Equal split of `availablePercent`; the remainder goes to the LAST share. */
export function splitEqually(
  availablePercent: number,
  count: number,
): number[] {
  if (count <= 0) return [];
  const total = percentToBasisPoints(availablePercent);
  const each = Math.floor(total / count);
  const remainder = total - each * count;
  return Array.from(
    { length: count },
    (_, index) => (each + (index === count - 1 ? remainder : 0)) / 100,
  );
}

/**
 * Percentages after one more shareholder joins (B6). A complete split (it
 * already sums to what is available) is rebalanced equally across everyone.
 * An incomplete, hand-made split is kept, and the newcomer starts at 0 so the
 * total never silently passes 100%.
 */
export function sharesAfterAdding(
  existingPercentages: readonly (number | null | undefined)[],
  availablePercent: number,
): number[] {
  if (existingPercentages.length === 0) return [roundPercent(availablePercent)];
  if (
    ownershipTotalBasisPoints(existingPercentages) ===
    percentToBasisPoints(availablePercent)
  ) {
    return splitEqually(availablePercent, existingPercentages.length + 1);
  }
  return [
    ...existingPercentages.map((percent) => roundPercent(percent ?? 0)),
    0,
  ];
}
